// ============================================================================
// marketProfile.ts — ПРОФИЛЬ КЛИЕНТА ВИТРИНЫ МАРКЕТПЛЕЙСА (SSoT/SRP).
//
// Что берём из существующих механизмов (без новых proto/сервера):
//   • имя, email, телефон, город — профиль пользователя (EntityService:
//     getEntity / updateEntity);
//   • статус подтверждения телефона — каналы связи пользователя
//     (listEntityChannels / sendVerificationCode / verifyChannel);
//   • «Мои адреса», район и настройки уведомлений — settingsStore
//     (server-backed user_settings).
//
// ОГРАНИЧЕНИЯ ДАННЫХ (зафиксировано, без моков/фоллбэков):
//   • в поставке hydra.market справочник mkt_clients НЕ связан с
//     пользователем (нет entity_id), поэтому профиль клиента не пишется в
//     mkt_clients;
//   • отдельного реквизита «район» в mkt_requests/mkt_clients нет — район
//     хранится в настройках профиля и подставляется в адрес заявки;
//   • отдельного справочника адресов клиента в поставке нет — адресная
//     книга хранится в user_settings (тот же механизм, что и остальные
//     персональные настройки) и подставляется в мастер заявки.
//
// Телефон подтверждается через канал «sms» (СМС-шлюз/провайдер базы):
// сервер принимает коды только для существующего канала связи. Если СМС
// не настроены, сервер вернёт явную ошибку — она показывается текстом.
// ============================================================================

import { generateId } from '@/lib/id';
import { settingsStore } from '@/lib/settingsStore';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import {
  friendlyError,
  isPermissionDeniedError,
  isTransportError,
} from '@/lib/errors';

// ---------- Настройки (ключи user_settings) ------------------------------------

export const MARKET_CLIENT_ADDRESSES_KEY = 'market.client.addresses';
export const MARKET_CLIENT_DISTRICT_KEY = 'market.client.district';

/** Каналы, которыми подтверждается телефон (SMS-шлюз/провайдер базы). */
const PHONE_CHANNEL_CODES = ['sms', 'sms_gateway', 'phone'];
const DEFAULT_PHONE_CHANNEL = 'sms';

// ---------- Профиль и каналы ---------------------------------------------------

export interface MarketChannelInfo {
  channel: string;
  value: string;
  isMain: boolean;
  isVerified: boolean;
  verifiedAt: string;
}

export type MarketPhoneStatus = 'verified' | 'unverified' | 'unknown' | 'empty';

export interface MarketClientProfile {
  userId: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  district: string;
  channels: MarketChannelInfo[];
  phoneChannel: MarketChannelInfo | null;
  phoneStatus: MarketPhoneStatus;
  phoneVerifiedAt: string;
  /** Каналы не удалось прочитать (статус телефона честно «неизвестен»). */
  channelsFailure: string | null;
}

export interface MarketContactInput {
  name: string;
  email: string;
  phone: string;
  city: string;
  district: string;
}

function channelFromRaw(raw: any): MarketChannelInfo {
  return {
    channel: String(raw?.channel || ''),
    value: String(raw?.value || ''),
    isMain: raw?.isMain === true || raw?.is_main === true,
    isVerified: raw?.isVerified === true || raw?.is_verified === true,
    verifiedAt: String(raw?.verifiedAt || raw?.verified_at || ''),
  };
}

function normalizePhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}

function isPhoneChannel(channel: string): boolean {
  return PHONE_CHANNEL_CODES.includes(channel);
}

function selectPhoneChannel(
  channels: MarketChannelInfo[],
  phone: string,
): MarketChannelInfo | null {
  const exact = channels.find(
    (c) => isPhoneChannel(c.channel) && normalizePhone(c.value) === normalizePhone(phone),
  );
  if (exact) return exact;
  return channels.find((c) => isPhoneChannel(c.channel)) || null;
}

/**
 * Профиль клиента: сущность пользователя + каналы связи + район из настроек.
 * Ошибка чтения профиля — исключение (UI покажет явную ошибку с повтором);
 * ошибка чтения каналов — отдельное поле channelsFailure (статус телефона
 * «неизвестен»), профиль не подменяется выдуманными данными.
 */
export async function loadMarketClientProfile(userId: string): Promise<MarketClientProfile> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  await settingsStore.hydrate();
  const sdk = getSDK();
  const entity: any = await sdk.entity.getEntity({ id: userId });

  let channels: MarketChannelInfo[] = [];
  let channelsFailure: string | null = null;
  try {
    const res: any = await sdk.entity.listEntityChannels({ entityId: userId });
    channels = ((res?.items || []) as any[]).map(channelFromRaw);
  } catch (e) {
    channelsFailure = friendlyError(e);
  }

  const phone = String(entity?.phone || '');
  const phoneChannel = selectPhoneChannel(channels, phone);
  let phoneStatus: MarketPhoneStatus;
  if (channelsFailure) phoneStatus = 'unknown';
  else if (!phone.trim()) phoneStatus = 'empty';
  else if (phoneChannel?.isVerified) phoneStatus = 'verified';
  else phoneStatus = 'unverified';

  return {
    userId,
    name: String(entity?.name || ''),
    email: String(entity?.email || ''),
    phone,
    city: String(entity?.city || ''),
    district: settingsStore.getJSON<string>(MARKET_CLIENT_DISTRICT_KEY, '') || '',
    channels,
    phoneChannel,
    phoneStatus,
    phoneVerifiedAt: phoneChannel?.verifiedAt || '',
    channelsFailure,
  };
}

/**
 * Сохранить контакты профиля: имя/email/телефон/город — в сущность,
 * район — в user_settings. Канал телефона пересоздаётся ТОЛЬКО при смене
 * номера (SetEntityChannels — полная замена и сбрасывает подтверждение
 * каналов; без изменения номера существующие подтверждения не трогаем).
 */
export async function saveMarketClientContacts(
  userId: string,
  current: MarketClientProfile,
  input: MarketContactInput,
): Promise<MarketClientProfile> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  const name = input.name.trim();
  if (!name) throw new Error('Укажите имя');
  const phone = input.phone.trim();
  const email = input.email.trim();
  const city = input.city.trim();
  const district = input.district.trim();

  // Сервер (UpdateEntity) не перезаписывает текстовые поля пустыми значениями:
  // очистка через профиль невозможна. Не делаем вид, что она сработала.
  if (!phone && current.phone.trim()) {
    throw new Error('Очистить телефон в профиле нельзя — укажите новый номер или оставьте прежний.');
  }
  if (!email && current.email.trim()) {
    throw new Error('Очистить электронную почту в профиле нельзя — укажите новый адрес.');
  }
  if (!city && current.city.trim()) {
    throw new Error('Очистить город в профиле нельзя — укажите новый город.');
  }

  const sdk = getSDK();
  await sdk.entity.updateEntity({
    id: userId,
    name,
    email,
    phone,
    city,
  } as any);

  if (phone !== current.phone.trim()) {
    const res: any = await sdk.entity.listEntityChannels({ entityId: userId });
    const other = ((res?.items || []) as any[])
      .map(channelFromRaw)
      .filter((c) => !isPhoneChannel(c.channel))
      .map((c) => ({ channel: c.channel, value: c.value, isMain: c.isMain }));
    const hasMain = other.some((c) => c.isMain);
    const channels = phone
      ? [{ channel: DEFAULT_PHONE_CHANNEL, value: phone, isMain: !hasMain }, ...other]
      : other;
    await sdk.entity.setEntityChannels({ entityId: userId, channels } as any);
  }

  settingsStore.setJSON(MARKET_CLIENT_DISTRICT_KEY, district);

  // Локальные представления профиля (шапка/чат/формы) — сразу с новыми данными.
  const user = useAuthStore.getState().user || {};
  useAuthStore.getState().setUser({ ...user, id: userId, name, email, phone, city } as any);
  const entity = useCenterStore.getState().entity;
  if (entity) {
    useCenterStore.setState({ entity: { ...entity, name, email } as any });
  }

  return loadMarketClientProfile(userId);
}

/**
 * Канал подтверждения телефона: если для номера ещё нет канала, создаём
 * «sms» (остальные каналы сохраняем). Возвращает канал для отправки кода.
 */
async function ensurePhoneChannel(userId: string, phone: string): Promise<MarketChannelInfo> {
  const value = phone.trim();
  if (!value) throw new Error('Сначала укажите телефон в профиле');
  const sdk = getSDK();
  const res: any = await sdk.entity.listEntityChannels({ entityId: userId });
  const channels = ((res?.items || []) as any[]).map(channelFromRaw);
  const existing = selectPhoneChannel(channels, value);
  if (existing && normalizePhone(existing.value) === normalizePhone(value)) return existing;

  const other = channels
    .filter((c) => !isPhoneChannel(c.channel))
    .map((c) => ({ channel: c.channel, value: c.value, isMain: c.isMain }));
  const hasMain = other.some((c) => c.isMain);
  const channel: MarketChannelInfo = {
    channel: DEFAULT_PHONE_CHANNEL,
    value,
    isMain: !hasMain,
    isVerified: false,
    verifiedAt: '',
  };
  await sdk.entity.setEntityChannels({
    entityId: userId,
    channels: [
      { channel: channel.channel, value: channel.value, isMain: channel.isMain },
      ...other,
    ],
  } as any);
  return channel;
}

/** Отправить код подтверждения телефона (канал «sms»/СМС-шлюз базы). */
export async function sendMarketPhoneCode(userId: string, phone: string): Promise<void> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  if (phone.replace(/\D/g, '').length < 10) {
    throw new Error('Укажите телефон для связи (не менее 10 цифр)');
  }
  const channel = await ensurePhoneChannel(userId, phone);
  await getSDK().entity.sendVerificationCode({
    entityId: userId,
    channel: channel.channel,
    value: channel.value,
  });
}

/** Проверить код подтверждения телефона. */
export async function verifyMarketPhoneCode(
  userId: string,
  phone: string,
  code: string,
): Promise<void> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  const value = code.trim();
  if (!value) throw new Error('Введите код из сообщения');
  const channel = await ensurePhoneChannel(userId, phone);
  await getSDK().entity.verifyChannel({
    entityId: userId,
    channel: channel.channel,
    value: channel.value,
    code: value,
  });
}

// ---------- Адресная книга (user_settings) ------------------------------------

export interface MarketSavedAddress {
  id: string;
  label: string;
  city: string;
  district: string;
  address: string;
  isDefault: boolean;
  updatedAt: number;
}

export interface MarketSavedAddressInput {
  id?: string;
  label?: string;
  city?: string;
  district?: string;
  address: string;
  isDefault?: boolean;
}

function addressFromRaw(raw: any): MarketSavedAddress | null {
  if (!raw || typeof raw !== 'object') return null;
  const address = String(raw.address || '').trim();
  if (!address) return null;
  return {
    id: String(raw.id || '') || generateId(),
    label: String(raw.label || '').trim() || address,
    city: String(raw.city || '').trim(),
    district: String(raw.district || '').trim(),
    address,
    isDefault: raw.isDefault === true || raw.is_default === true,
    updatedAt: Number(raw.updatedAt || raw.updated_at || 0) || 0,
  };
}

function persistAddresses(list: MarketSavedAddress[]): void {
  settingsStore.setJSON(MARKET_CLIENT_ADDRESSES_KEY, list);
}

/** Адреса клиента (порядок: основной первым, затем свежие). */
export function loadMarketAddresses(): MarketSavedAddress[] {
  const raw = settingsStore.getJSON<unknown>(MARKET_CLIENT_ADDRESSES_KEY, []);
  const list = Array.isArray(raw)
    ? raw.map(addressFromRaw).filter((a): a is MarketSavedAddress => !!a)
    : [];
  return list.sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return b.updatedAt - a.updatedAt;
  });
}

/** Полный адрес одной строкой (для поля заявки). */
export function composeMarketAddress(
  address: Pick<MarketSavedAddress, 'city' | 'district' | 'address'>,
): string {
  return [address.city, address.district, address.address]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
}

function normalizeAddressText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function deriveAddressLabel(text: string): string {
  const first = text.split('\n').map((s) => s.trim()).find(Boolean) || text.trim();
  return first.length > 60 ? `${first.slice(0, 57)}…` : first;
}

/**
 * Добавить/обновить адрес. Дедупликация — по полному тексту адреса:
 * повторное сохранение не плодит дубли, а обновляет подпись/район.
 */
export function saveMarketAddress(input: MarketSavedAddressInput): MarketSavedAddress[] {
  const text = input.address.trim();
  if (text.length < 5) throw new Error('Укажите адрес (не короче 5 символов)');
  const city = String(input.city || '').trim();
  const district = String(input.district || '').trim();
  const composed = composeMarketAddress({ city, district, address: text });
  const list = loadMarketAddresses();
  const existing = input.id
    ? list.find((a) => a.id === input.id)
    : list.find((a) => normalizeAddressText(composeMarketAddress(a)) === normalizeAddressText(composed));
  const id = existing?.id || input.id || generateId();
  const hasDefault = list.some((a) => a.id !== id && a.isDefault);
  const entry: MarketSavedAddress = {
    id,
    label: String(input.label || '').trim() || deriveAddressLabel(composed),
    city,
    district,
    address: text,
    isDefault: Boolean(input.isDefault) || !hasDefault || !!existing?.isDefault,
    updatedAt: Date.now(),
  };
  const next = existing
    ? list.map((a) => (a.id === id ? entry : a))
    : [...list, entry];
  if (entry.isDefault) {
    for (const a of next) {
      if (a.id !== id) a.isDefault = false;
    }
  }
  persistAddresses(next);
  return loadMarketAddresses();
}

export function deleteMarketAddress(id: string): MarketSavedAddress[] {
  const list = loadMarketAddresses();
  const removed = list.find((a) => a.id === id);
  const next = list.filter((a) => a.id !== id);
  if (removed?.isDefault && next.length > 0) next[0].isDefault = true;
  persistAddresses(next);
  return loadMarketAddresses();
}

export function setDefaultMarketAddress(id: string): MarketSavedAddress[] {
  const list = loadMarketAddresses();
  const found = list.some((a) => a.id === id);
  if (!found) throw new Error('Адрес не найден — обновите список');
  const next = list.map((a) => ({ ...a, isDefault: a.id === id, updatedAt: a.id === id ? Date.now() : a.updatedAt }));
  persistAddresses(next);
  return loadMarketAddresses();
}

/** Быстро запомнить адрес из мастера заявки (подпись — по тексту адреса). */
export function rememberMarketAddressFromText(text: string): MarketSavedAddress[] {
  const value = text.trim();
  if (value.length < 5) return loadMarketAddresses();
  return saveMarketAddress({ address: value, label: deriveAddressLabel(value) });
}

// ---------- Настройки уведомлений/чатов (user_settings) -----------------------

export type MarketNotifyKey =
  | 'market'
  | 'marketInApp'
  | 'chat'
  | 'soundEnabled'
  | 'soundChat'
  | 'soundNotify';

export interface MarketNotifySettings {
  market: boolean;
  marketInApp: boolean;
  chat: boolean;
  soundEnabled: boolean;
  soundChat: boolean;
  soundNotify: boolean;
}

const NOTIFY_KEYS: Record<MarketNotifyKey, string> = {
  market: 'notifications.market_requests',
  marketInApp: 'notifications.market_requests_inapp',
  chat: 'chat.notify',
  soundEnabled: 'sound.enabled',
  soundChat: 'sound.chat_enabled',
  soundNotify: 'sound.notify_enabled',
};

export function loadMarketNotifySettings(): MarketNotifySettings {
  return {
    market: settingsStore.getJSON<boolean>(NOTIFY_KEYS.market, true) !== false,
    marketInApp: settingsStore.getJSON<boolean>(NOTIFY_KEYS.marketInApp, true) !== false,
    chat: settingsStore.getJSON<boolean>(NOTIFY_KEYS.chat, true) !== false,
    soundEnabled: settingsStore.getJSON<boolean>(NOTIFY_KEYS.soundEnabled, true) !== false,
    soundChat: settingsStore.getJSON<boolean>(NOTIFY_KEYS.soundChat, true) !== false,
    soundNotify: settingsStore.getJSON<boolean>(NOTIFY_KEYS.soundNotify, true) !== false,
  };
}

/** Изменение сохраняется сразу (settingsStore: кэш + сервер user_settings). */
export function setMarketNotifySetting(key: MarketNotifyKey, value: boolean): void {
  settingsStore.setJSON(NOTIFY_KEYS[key], value);
}

export function subscribeMarketNotifySettings(fn: () => void): () => void {
  return settingsStore.subscribe(fn);
}

// ---------- Ошибка профиля -----------------------------------------------------

export interface MarketProfileFailure {
  kind: 'permission' | 'not-found' | 'offline' | 'other';
  message: string;
}

export function toMarketProfileFailure(e: unknown): MarketProfileFailure {
  if (isPermissionDeniedError(e)) {
    return { kind: 'permission', message: friendlyError(e) };
  }
  if (isTransportError(e)) {
    return { kind: 'offline', message: friendlyError(e) };
  }
  // NotFound без импорта отдельного хелпера: сообщение сервера уже понятное.
  if (String(friendlyError(e)).toLowerCase().includes('не найден')) {
    return { kind: 'not-found', message: 'Профиль не найден — войдите заново.' };
  }
  return { kind: 'other', message: friendlyError(e) };
}

// Намеренно не глушим ошибки профиля/каналов — интерфейс показывает их текстом.
